// Geometry for the double-helix photo gallery.
//
// Ported from githyperplexed/double-helix-gallery (kept read-only under
// reference_projects/double-helix-gallery-main) — deliberately the SAME formulas,
// because the look is the whole point and this is the part that produces it.
//
// Pure and DOM-free on purpose. The render loop is not the only thing that needs
// to know where a card is: the "photos gather into the cylinder, then drain into
// the folder" sequence has to hand off from exactly the position the card is
// already at, or it jumps. One formula, two callers.
//
// The illusion, for whoever tunes this next:
//
//   • cards sit ON the outside of a cylinder (rotate to an angle, then push out
//     by the radius). The far ones are therefore seen from behind;
//   • you only read that as "seeing inside the tube" when the far side is dark
//     and blurred enough to stop you noticing the image is mirrored — that is
//     what depthFloor and maxBlur buy, not just atmosphere;
//   • perspective has to be close to the radius (the original runs about 2.5x)
//     or the cylinder flattens into a ring;
//   • the strand is taller than its container (spanFactor) so cards enter and
//     leave past the edges rather than popping in mid-air.
//
// WHAT USED TO BE HERE. The port arrived with `wrap`, `progressAt`, `helixAngle`
// and `helixTransform` — the machinery of an endless descent, where a card ran
// off the bottom and reappeared at the top, and `turns` had to be a whole number
// or the seam showed. None of that survived the vortex (see radiusScaleAt in
// sequence.js): cards now hold their heights and the cylinder turns as one body,
// so nothing wraps, `turns` is free to be fractional, and every position is a
// world coordinate built by helixWorld. The functions were deleted 2026-08-02
// with their tests, which by then were pinning rules the animation no longer
// obeyed. They are in the git history if the descent is ever wanted back.

export const TAU = Math.PI * 2;

/** Phase offset putting each strand on its own side of the axis (2 strands = 180°). */
export const strandPhase = (strand, strands) => (strand / strands) * TAU;

/** 0 = far side of the cylinder, 1 = nearest the camera. */
export const depthOf = (angle) => (Math.cos(angle) + 1) / 2;

/** Vertical offset for a progress value across a strand `span` px tall. */
export const helixY = (progress, span) => (progress - 0.5) * span;

/** Linear blend. */
export const lerp = (from, to, t) => from + (to - from) * t;

/**
 * Where a card sits in space, rather than how to write it as a transform.
 *
 * This is the hinge the whole thing turns on. `rotateY(a) translateZ(R)` and
 * `translate3d(R·sin a, 0, R·cos a) rotateY(a)` are the SAME matrix — rotating
 * then pushing along the rotated axis lands exactly where pushing along the
 * world axes and then rotating in place does. Writing it the second way costs
 * nothing and buys everything, because a position in world coordinates can be
 * blended with any other position, and a rotateY/translateZ chain cannot.
 *
 * That is what lets a photo start life at its rectangle on the Files tab — a flat
 * screen position with no rotation — and arrive on the cylinder: the two are just
 * different numbers handed to the same builder.
 */
export const helixWorld = (angle, y, radius) => ({
  x: Math.sin(angle) * radius,
  y,
  z: Math.cos(angle) * radius,
});

/**
 * The one transform builder. Everything the card ever does — sitting on the page,
 * flying to the cylinder, turning on it, pouring into the folder — is this with
 * different numbers, so no two states can drift apart in how they are expressed.
 */
export const cardTransform = ({ x, y, z, angle = 0, scale = 1 }) =>
  `translate(-50%, -50%) translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, ${z.toFixed(1)}px)` +
  ` rotateY(${angle.toFixed(4)}rad) scale(${scale.toFixed(3)})`;

/**
 * Depth cueing. Both curves are squared rather than linear, which holds the
 * near half bright and sends the far half away quickly — a linear fade leaves
 * the back of the cylinder legible enough to read as mirrored.
 */
export function depthFilter(depth, { mode = "dark", floor = 0.12, maxBlur = 5 } = {}) {
  const near = floor + (1 - floor) * depth * depth;
  const blur = (1 - depth) * (1 - depth) * maxBlur;

  // On a LIGHT backdrop the cueing has to invert, and this is the one place the
  // whole illusion can be broken by a reasonable-looking change.
  //
  // The far side of the cylinder is the BACK of a photo — mirrored. On black,
  // dimming it to 12% hides that, because dark on dark is nothing. Do the same on
  // white and those cards become the highest-contrast thing on the screen and the
  // mirroring is all you can see. So distance has to be spent FADING TOWARD the
  // background instead of away from it: less opacity, less colour, more blur.
  // That is aerial perspective, which is how distance reads in daylight anyway.
  if (mode === "light") {
    // The curve is SQUARE-ROOTED here, not squared, and that is not a tweak — the
    // squared version made the whole cylinder disappear (Robert's screenshots,
    // 2026-08-01). Squaring holds a card near the floor until it is almost dead
    // in front of you: at side-on it was already down to 35%. On black that is
    // fine, because a dimmed photo is still a shape. On white it is fatal, and
    // doubly so here, because claim photos ARE pale — white walls, cream carpet,
    // a white washing machine. A pale photo at 35% over near-white is nothing at
    // all. The only cards that read were the PDFs, and only because a red icon on
    // a bordered tile has contrast to spare.
    //
    // Rooting it inverts that: full strength across the front and the sides, and
    // the fade spent only on the half that is genuinely facing away.
    const seen = floor + (1 - floor) * Math.sqrt(depth);
    return `opacity(${seen.toFixed(3)}) saturate(${(0.45 + 0.55 * depth).toFixed(3)}) blur(${blur.toFixed(2)}px)`;
  }
  return `brightness(${near.toFixed(3)}) blur(${blur.toFixed(2)}px)`;
}
